import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { Spinner } from '../../shared/ui/Spinner';
import { Button } from '../../shared/ui/Button';
import { useAuth } from './useAuth';
import { useLicense } from '../license/useLicense';
import { ContractEndedScreen } from '../license/ContractEndedScreen';
import { SessionConflictScreen, SessionDisplacedScreen, SessionErrorScreen } from './SessionScreens';

const centered = {
  display: 'grid',
  placeItems: 'center',
  minHeight: '100vh',
  background: 'var(--color-background)',
} as const;

export function RequireAuth() {
  const {
    status, userDoc, userDocLoaded, signOut,
    session, sessionConflict, resolveSessionConflict, retrySession,
  } = useAuth();
  const location = useLocation();
  const { cut } = useLicense();

  if (status === 'loading') {
    return (
      <div style={centered}>
        <Spinner size="lg" label="Verificando autenticação" />
      </div>
    );
  }

  if (status === 'signed-out') {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Contrato encerrado: antes do gate de sessao, o claimSession tambem e recusado no corte.
  if (cut) return <ContractEndedScreen onSignOut={() => void signOut()} />;

  // P55 — gate de conta desativada.
  //
  // O AuthProvider mantém um onSnapshot vivo em users/{uid}, então desativar
  // alguém derruba a tela DELE na hora, sem esperar o token expirar. Sem este
  // gate a sidebar montava inteira (roles/{roleId} segue legível) e a pessoa
  // navegava por telas vazias e toasts de erro, sem nunca saber o motivo.
  //
  // `userDoc == null` NÃO entra aqui de propósito: doc ausente é o estado do
  // bootstrap, e bloquear nele trancaria a criação do primeiro admin.
  if (userDocLoaded && userDoc && userDoc.status !== 'active') {
    return (
      <div style={centered}>
        <div style={{ display: 'grid', gap: '1rem', textAlign: 'center', maxWidth: '28rem', padding: '2rem' }}>
          <h1 style={{ fontSize: '1.25rem', margin: 0 }}>Conta desativada</h1>
          <p style={{ margin: 0, color: 'var(--color-text-secondary)' }}>
            Seu acesso ao sistema foi desativado. Fale com a administração da clínica.
          </p>
          <div>
            <Button variant="ghost" onClick={() => void signOut()}>Sair</Button>
          </div>
        </div>
      </div>
    );
  }

  // P92 — vaga de sessão. Nada abaixo daqui monta antes de 'ok': as rules recusam
  // quem ainda não reivindicou a vaga, e onSnapshot negado não se recupera.
  if (session === 'checking') {
    return (
      <div style={centered}>
        <Spinner size="lg" label="Verificando sessão" />
      </div>
    );
  }
  if (session === 'conflict') {
    return (
      <SessionConflictScreen
        slot={sessionConflict?.slot ?? 'computador'}
        since={sessionConflict?.since ?? 0}
        onResolve={(takeOver) => void resolveSessionConflict(takeOver)}
      />
    );
  }
  if (session === 'displaced') return <SessionDisplacedScreen onSignOut={() => void signOut()} />;
  if (session === 'error') {
    return <SessionErrorScreen onRetry={() => void retrySession()} onSignOut={() => void signOut()} />;
  }

  return <Outlet />;
}
